import { useEffect } from "react";
import { motion } from "framer-motion";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_8a8d0b4b-d26f-4b82-a717-f2ce98e43ad0/artifacts/sg857t0m_WhatsApp%20Image%202026-06-27%20at%2016.55.55%20%281%29.webp";
const PROFILE_IMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_golden-wolf-premium/artifacts/0ag6fthu_WhatsApp%20Image%202026-09-01%20at%2021.43.26.jpeg";

const chapters = [
  { n: "01", title: "The Beginning", body: "Born into a lineage of dog trainers — a third-generation craft that shaped his understanding of leadership, discipline and the language beneath language." },
  { n: "02", title: "The Wolfkeeper Years", body: "More than two decades in the field. Four thousand dogs. Aggression cases, service work, K9 rehab, and the founding of Wolfkeeper University as a formal school of the craft." },
  { n: "03", title: "Building Businesses & Brands", body: "Founder-level work across entertainment, marketing, logistics, product development and technology — turning field-tested leadership into strategy that founders can use." },
  { n: "04", title: "Technology & Innovation", body: "An early adopter of AI for small business — coaching entrepreneurs, animal-care professionals and nonprofits to use technology without losing their humanity." },
  { n: "05", title: "Veterans & Social Impact", body: "The Golden Wolf Foundation — supporting veterans through service-dog training, workforce pathways and community programming that heals through purpose." },
  { n: "06", title: "Chicago to Nairobi", body: "A Chicago flagship and an East African vision — carrying leadership, animal husbandry and cultural entrepreneurship across continents." },
  { n: "07", title: "The Legacy Ahead", body: "Books, media, education and community — the next chapter for men, families and organizations rebuilding on their own terms." },
];

const pillars = [
  { n: "I", label: "Animal Psychology" },
  { n: "II", label: "Leadership" },
  { n: "III", label: "Education" },
  { n: "IV", label: "Technology & AI" },
  { n: "V", label: "Wealth Creation" },
  { n: "VI", label: "Media & Storytelling" },
  { n: "VII", label: "Legacy & Philanthropy" },
];

export default function About() {
  useEffect(() => { document.title = "About — Toriano Sanzone · Master Dog Trainer, Founder, Strategist"; window.scrollTo(0, 0); }, []);
  return (
    <div className="pt-[120px] md:pt-[140px]" data-testid="about-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden spotlight">
              <img src={PROFILE_IMG} alt="Toriano Sanzone portrait" className="w-full h-full object-cover object-[center_15%]"/>
              <div className="absolute inset-0 border border-gold/30" />
              <div className="absolute top-4 left-4 text-hair text-gold z-10">Portrait 001</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-eyebrow text-gold">About Toriano</div>
            <h1 className="font-display text-[clamp(2.4rem,6vw,6.4rem)] leading-[0.92] tracking-[-0.03em] mt-4">
              Not one title.<br/><span className="italic gold-foil">One vision.</span>
            </h1>
            <div className="mt-8 space-y-6 max-w-2xl text-bone/85 font-editorial text-lg leading-relaxed">
              <p>Toriano Sanzone does not fit inside one title. He is a third-generation Master Dog Trainer, founder, author, educator, strategist, creative director and community builder. For more than 25 years, he has used leadership, psychology, discipline, storytelling and innovation to transform dogs, people, organizations and neighborhoods.</p>
              <p>His work bridges the ancient trade of animal husbandry with modern technology, luxury design and the discipline of abundance — from a Chicago flagship to an East African vision.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section bg-ink-2">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="text-eyebrow text-gold mb-8">The Timeline</div>
          <div className="grid gap-12">
            {chapters.map((c, i) => (
              <motion.article
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="grid md:grid-cols-12 gap-6 items-start border-t border-hair pt-8"
                data-testid={`about-chapter-${c.n}`}
              >
                <div className="md:col-span-3">
                  <div className="chapter-num text-6xl md:text-7xl">{c.n}</div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-display text-3xl md:text-4xl leading-tight">{c.title}</h3>
                  <p className="mt-4 text-bone/75 font-editorial text-lg max-w-2xl">{c.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Seven Pillars */}
      <section className="section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-4">
              <div className="text-eyebrow text-gold">The Seven Pillars</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-[clamp(2.2rem,5.5vw,5.5rem)] leading-[0.95] tracking-[-0.02em]">
                Seven pillars of <span className="italic gold-foil">Toriano Sanzone.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pillars.map((p, i) => (
              <motion.div key={p.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.06 * i }}
                className="p-6 border border-hair hover:border-gold transition-colors bg-ink-2"
                data-testid={`pillar-${p.n}`}
              >
                <div className="chapter-num text-4xl">{p.n}</div>
                <div className="mt-3 font-display italic text-xl">{p.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
